/** Track level under the master gain, so the effect cues stay readable over it. */
const LEVEL = 0.3;
const FADE_IN_S = 0.8;
const FADE_OUT_S = 0.35;

export interface MusicState {
  inMatch: boolean;
  hidden: boolean;
  muted: boolean;
  music: boolean;
}

export function musicShouldPlay(state: MusicState): boolean {
  return state.inMatch && state.music && !state.muted && !state.hidden;
}

export function musicNeedsKick(playing: boolean, elementPaused: boolean | null): boolean {
  if (!playing) return true;
  return elementPaused !== false;
}

/** Looping match track. Streamed through an element: decoding it whole holds ~70 MB of PCM. */
export class Music {
  private readonly state: MusicState = {
    inMatch: false,
    hidden: false,
    muted: false,
    music: true,
  };
  private el: HTMLAudioElement | null = null;
  private gain: GainNode | null = null;
  private playing = false;
  private starting = false;
  private pauseTimer = 0;

  constructor(
    private readonly src: string,
    private readonly bus: () => { ctx: AudioContext; out: AudioNode },
  ) {}

  apply(settings: { muted: boolean; music: boolean }): void {
    this.state.muted = settings.muted;
    this.state.music = settings.music;
    this.sync();
  }

  setInMatch(on: boolean): void {
    this.state.inMatch = on;
    this.sync();
  }

  setHidden(hidden: boolean): void {
    this.state.hidden = hidden;
    this.sync();
  }

  /** Drops the track for a blast, then eases back. */
  duck(): void {
    if (!this.playing || !this.gain) return;
    const ctx = this.bus().ctx;
    const param = this.gain.gain;
    const now = ctx.currentTime;
    const dipped = LEVEL * 0.15;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.linearRampToValueAtTime(dipped, now + 0.03);
    param.linearRampToValueAtTime(dipped, now + 1.1);
    param.linearRampToValueAtTime(LEVEL, now + 1.8);
  }

  /** Also called on user gestures: browsers refuse play() until the page has had one. */
  sync(): void {
    if (!musicShouldPlay(this.state)) {
      this.stop();
      return;
    }
    const paused = this.el ? this.el.paused : null;
    if (!musicNeedsKick(this.playing, paused) || this.starting) return;
    this.playing = false;
    this.start();
  }

  private start(): void {
    if (this.playing || this.starting) return;
    const { ctx, out } = this.bus();
    const el = this.el ?? this.create(ctx, out);
    window.clearTimeout(this.pauseTimer);
    this.starting = true;
    const kick = () => {
      this.starting = false;
      if (!musicShouldPlay(this.state)) return;
      this.playing = true;
      this.ramp(ctx, LEVEL, FADE_IN_S);
      void el.play().catch(() => {
        this.playing = false;
      });
    };
    if (ctx.state === 'running') kick();
    else
      void ctx
        .resume()
        .then(kick)
        .catch(() => {
          this.starting = false;
        });
  }

  private stop(): void {
    this.starting = false;
    const el = this.el;
    if (!this.playing || !el) return;
    this.playing = false;
    this.ramp(this.bus().ctx, 0, FADE_OUT_S);
    window.clearTimeout(this.pauseTimer);
    this.pauseTimer = window.setTimeout(() => el.pause(), FADE_OUT_S * 1000 + 50);
  }

  private create(ctx: AudioContext, out: AudioNode): HTMLAudioElement {
    const el = new Audio(this.src);
    el.loop = true;
    el.preload = 'auto';
    const gain = ctx.createGain();
    gain.gain.value = 0;
    ctx.createMediaElementSource(el).connect(gain).connect(out);
    this.el = el;
    this.gain = gain;
    return el;
  }

  private ramp(ctx: AudioContext, to: number, seconds: number): void {
    const param = this.gain?.gain;
    if (!param) return;
    const now = ctx.currentTime;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.linearRampToValueAtTime(to, now + seconds);
  }
}
